import { createTheme, ThemeOptions } from '@mui/material'

export enum EColorScheme {
  LIGHT = 'light',
  DARK = 'dark',
}

// Status colors for uptime events
export const statusColors = {
  OPERATIONAL: '#42ba96', // limeGreen
  DEGRADED: '#E6B10E', // accentYellow
  MAINTENANCE: '#0D9DDB', // accentBlue
  OUTAGE: '#f44336', // error red
}

const verbColors = {
  error: {
    main: '#f44336',
  },
  info: {
    main: '#79ABD2',
  },
  success: {
    main: '#388e3c',
    light: '#57ab6d',
  },
  warning: {
    main: '#f57c00',
  },
}

const lightPalette = {
  mode: 'light' as const,
  primary: {
    main: '#4492c6',
    dark: '#2f668a',
  },
  secondary: {
    main: '#00263a',
    dark: '#00131d',
    light: '#004367',
  },
  divider: '#e0e0e0',
  background: {
    paper: '#ffffff',
    default: '#E1EAF2',
  },
  ...verbColors,
}

const darkPalette = {
  mode: 'dark' as const,
  primary: {
    main: '#4492c6',
    dark: '#2f668a',
  },
  secondary: {
    main: '#E2EAF2',
    dark: '#a7bfd7',
    light: '#FFFFFF',
  },
  text: {
    primary: '#ffffff',
  },
  divider: '#00131d',
  background: {
    default: '#00131d',
    paper: '#00263a',
  },
  ...verbColors,
}

const baseThemeOptions: Omit<ThemeOptions, 'palette'> = {
  shape: {
    borderRadius: 4,
  },
  spacing: 8,
  typography: {
    fontFamily:
      '"Inter", "Proxima Nova", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: {
      fontSize: '48px',
      fontWeight: 700,
    },
    h2: {
      fontSize: '36px',
      fontWeight: 600,
    },
    h3: {
      fontSize: '28px',
      fontWeight: 600,
    },
    h4: {
      fontSize: '24px',
      fontWeight: 600,
    },
    h5: {
      fontSize: '20px',
      fontWeight: 600,
    },
    h6: {
      fontSize: '18px',
      fontWeight: 600,
    },
    subtitle1: {
      fontSize: '16px',
    },
    subtitle2: {
      fontSize: '14px',
    },
    body1: {
      fontSize: '16px',
    },
    body2: {
      fontSize: '14px',
    },
    caption: {
      fontSize: '12px',
    },
    overline: {
      fontSize: '12px',
    },
  },
  components: {
    MuiButton: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
  },
}

export const createAppTheme = (mode: EColorScheme) => {
  return createTheme({
    ...baseThemeOptions,
    palette: mode === EColorScheme.DARK ? darkPalette : lightPalette,
  })
}
